// src/pages/admin/AdminPermissionsPage.tsx
import React, { useEffect, useState } from 'react';
import {
    Permission,
    listPermissions,
    createPermission,
    updatePermission,
    deletePermission,
} from 'src/features/admin/api';
import { useToast } from 'src/shared/ui/toast/useToast';

const AdminPermissionsPage: React.FC = () => {
    const toast = useToast();

    const [rows, setRows] = useState<Permission[]>([]);
    const [loading, setLoading] = useState(false);

    const [keyInput, setKeyInput] = useState('');
    const [descInput, setDescInput] = useState('');
    const [catInput, setCatInput] = useState('');

    const reload = async () => {
        setLoading(true);
        try {
            const list = await listPermissions();
            setRows(list);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void reload();
    }, []);

    const handleCreate = async () => {
        if (!keyInput.trim()) {
            toast.warning('Permission key를 입력해 주세요.', {
                title: '입력 필요',
            });
            return;
        }
        setLoading(true);
        try {
            const created = await createPermission({
                key: keyInput.trim(),
                description: descInput.trim() || undefined,
                category: catInput.trim() || undefined,
            });
            setRows((prev) => [...prev, created]);
            setKeyInput('');
            setDescInput('');
            setCatInput('');

            toast.success(`Permission "${created.key}"를 추가했습니다.`, {
                title: '추가 완료',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (p: Permission) => {
        if (!window.confirm(`정말 "${p.key}" 퍼미션을 삭제하시겠습니까?`)) return;
        setLoading(true);
        try {
            await deletePermission(p.id);
            setRows((prev) => prev.filter((x) => x.id !== p.id));
            toast.success(`Permission "${p.key}"를 삭제했습니다.`, {
                title: '삭제 완료',
            });
        } catch (e: any) {
            toast.error('삭제할 수 없습니다. (Role에 연결되어 있을 수 있습니다)', {
                title: '삭제 실패',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleQuickEdit = async (p: Permission) => {
        const newDesc = window.prompt('설명(description)을 수정하세요', p.description ?? '');
        if (newDesc === null) return;
        const newCat = window.prompt('카테고리(category)를 수정하세요', p.category ?? '');
        if (newCat === null) return;

        setLoading(true);
        try {
            const updated = await updatePermission(p.id, {
                description: newDesc || undefined,
                category: newCat || undefined,
            });
            setRows((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
            toast.success(`Permission "${updated.key}"를 수정했습니다.`, {
                title: '수정 완료',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold text-gray-900">Permissions</h1>
                {loading && <span className="text-xs text-gray-500">Loading...</span>}
            </div>

            {/* 새 Permission 추가 */}
            <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-end">
                <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">Key</label>
                    <input
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="예: wbs.publish"
                        value={keyInput}
                        onChange={(e) => setKeyInput(e.target.value)}
                    />
                </div>
                <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">Description</label>
                    <input
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="WBS 퍼블리시 권한"
                        value={descInput}
                        onChange={(e) => setDescInput(e.target.value)}
                    />
                </div>
                <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1">Category</label>
                    <input
                        className="w-full border rounded px-2 py-1 text-sm"
                        placeholder="예: WBS, PROJECT, PAYMENT"
                        value={catInput}
                        onChange={(e) => setCatInput(e.target.value)}
                    />
                </div>
                <button
                    className="px-3 py-2 rounded bg-sky-600 text-white text-sm hover:bg-sky-700"
                    onClick={handleCreate}
                >
                    추가
                </button>
            </div>

            {/* 목록 */}
            <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
                <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide">
                        <tr>
                            <th className="px-3 py-2 text-left">Key</th>
                            <th className="px-3 py-2 text-left">Category</th>
                            <th className="px-3 py-2 text-left">Description</th>
                            <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={4} className="px-3 py-6 text-center text-gray-400">
                                    아직 등록된 Permission이 없습니다.
                                </td>
                            </tr>
                        )}
                        {rows.map((p) => (
                            <tr key={p.id} className="border-t">
                                <td className="px-3 py-2 font-mono text-xs">{p.key}</td>
                                <td className="px-3 py-2 text-xs text-gray-600">{p.category ?? '-'}</td>
                                <td className="px-3 py-2 text-xs text-gray-700">{p.description ?? '-'}</td>
                                <td className="px-3 py-2 text-right space-x-2">
                                    <button
                                        className="text-xs text-sky-700 hover:underline"
                                        onClick={() => handleQuickEdit(p)}
                                    >
                                        수정
                                    </button>
                                    <button
                                        className="text-xs text-rose-600 hover:underline"
                                        onClick={() => handleDelete(p)}
                                    >
                                        삭제
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminPermissionsPage;
